/** SUNTRONIX ONE — Assignee typeahead.
 *
 *  Debounced live search across employees + companies + master companies.
 *  Used from the Bulk-upload inline Fix editor and the Multi-Row Fix bar.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const KIND_TONE = {
  employee:        { dot: "bg-emerald-500", label: "Employee"        },
  company:         { dot: "bg-sky-500",     label: "Client company"  },
  master_company:  { dot: "bg-purple-500",  label: "Master company"  },
};

export default function AssigneeTypeahead({
  value = "",
  onChange,
  onPick,
  masterCompanyId,
  companyId,
  testId = "assignee-typeahead",
  placeholder = "Search employee, client, master…",
  autoFocus = false,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIx, setActiveIx] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  useEffect(() => {
    const q = (value || "").trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 1) { setItems([]); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/assignee-search/", { params: {
          q, master_company_id: masterCompanyId, company_id: companyId,
          limit: 8,
        }});
        setItems(data.items || []);
        setActiveIx(0);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 200);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value, masterCompanyId, companyId]);

  const commit = (it) => {
    onChange?.(it.label);
    onPick?.(it);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { setActiveIx((i) => (i + 1) % items.length); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setActiveIx((i) => (i - 1 + items.length) % items.length); e.preventDefault(); }
    else if (e.key === "Enter") { commit(items[activeIx]); e.preventDefault(); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onKeyDown={onKey}
        placeholder={placeholder}
        className="w-full border rounded px-1.5 py-1 text-[12px]"
        data-testid={testId}
      />
      {open && (loading || items.length > 0) && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 min-w-[240px] bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto"
              data-testid={`${testId}-dropdown`}>
          {loading && items.length === 0 && (
            <div className="px-2 py-1.5 text-[11px] text-slate-400">Searching…</div>
          )}
          {items.map((it, ix) => {
            const t = KIND_TONE[it.kind] || KIND_TONE.employee;
            return (
              <button
                key={`${it.kind}-${it.id}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(it)}
                onMouseEnter={() => setActiveIx(ix)}
                data-testid={`${testId}-item-${it.kind}-${it.id}`}
                className={`w-full text-left px-2 py-1.5 flex items-center gap-2 transition
                            ${activeIx === ix ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                <span className={`inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${t.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-medium text-slate-900 truncate">
                    {it.label}
                  </div>
                  {it.sublabel && (
                    <div className="text-[10px] text-slate-500 truncate">
                      {it.sublabel}
                    </div>
                  )}
                </div>
                <span className="text-[9px] uppercase tracking-wider text-slate-500 ml-auto flex-shrink-0">
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
